import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const cache = new Map();
let pending = 0;
const subs = new Set();
const bump = (n) => {
  pending += n;
  subs.forEach((f) => f(pending));
};

export function useUploadsPending() {
  const [n, setN] = useState(pending);
  useEffect(() => {
    subs.add(setN);
    return () => {
      subs.delete(setN);
    };
  }, []);
  return n;
}

export function useDraft(key, init) {
  const [v, setV] = useState(() => {
    try { return { ...init, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch { return init; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(v)); }, [key, v]);
  return [v, setV, () => localStorage.removeItem(key)];
}

async function compress(file) {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close?.();
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.82));
    c.width = 0;
    c.height = 0;
    return blob ? new File([blob], "photo.jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

export async function uploadPhoto(file, kind = "photo") {
  const fd = new FormData();
  fd.append("file", file.type === "application/pdf" ? file : await compress(file));
  fd.append("kind", kind);
  const { data } = await api.post("/files", fd);
  return data.id;
}

export async function openFile(id) {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(`/files/${id}`, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    if (w) w.location.href = u;
    else window.location.href = u;
  } catch (e) {
    w?.close();
    toast.error(errMsg(e));
  }
}

export function SecureImg({ id, className = "", alt = "Photo", onClick, testid, fallback }) {
  const [src, setSrc] = useState(cache.get(id));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (!id || cache.has(id)) {
      setSrc(cache.get(id));
      return;
    }
    let live = true;
    api.get(`/files/${id}`, { responseType: "blob" }).then((r) => {
      const u = URL.createObjectURL(r.data);
      cache.set(id, u);
      if (live) setSrc(u);
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);
  if (!src) return <div className={`bg-slate-100 animate-pulse ${className}`} data-testid={testid} />;
  if (bad && fallback) return fallback;
  return <img src={src} alt={alt} className={`object-cover ${className}`} onClick={onClick} onError={() => setBad(true)} data-testid={testid} />;
}

export function Lightbox({ id, onClose }) {
  if (!id) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-2 bg-black border-0" data-testid="photo-lightbox">
        <SecureImg id={id} className="w-full max-h-[85vh] object-contain" />
      </DialogContent>
    </Dialog>
  );
}

export function useUploader(onAdd, kind = "photo") {
  const [busy, setBusy] = useState(0);
  const handle = async (files) => {
    const list = Array.from(files || []);
    setBusy((b) => b + list.length);
    bump(list.length);
    for (const f of list) {
      try {
        onAdd(await uploadPhoto(f, kind));
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy((b) => b - 1);
        bump(-1);
      }
    }
  };
  return { busy, handle };
}

export function Thumb({ id, onRemove, onOpen, testid, children }) {
  return (
    <div className="relative group" data-testid={testid}>
      <SecureImg id={id} className="w-full aspect-square rounded-md border cursor-zoom-in" onClick={() => onOpen?.(id)} />
      {onRemove && (
        <button type="button" onClick={() => onRemove(id)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white grid place-items-center shadow" data-testid={`${testid}-remove`} aria-label="Supprimer la photo">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {children}
    </div>
  );
}

export function PhotoSlot({ label, ids, onChange, testid, kind = "photo", accept = "image/*", single }) {
  const ref = useRef();
  const [open, setOpen] = useState(null);
  const { busy, handle } = useUploader((id) => onChange((cur) => (single ? [id] : [...cur, id])), kind);
  const has = ids.length > 0;
  return (
    <div className={`rounded-xl border-2 ${has ? "border-emerald-400 bg-emerald-50/40" : "border-dashed border-slate-300 bg-white"} p-2.5 flex flex-col gap-2`} data-testid={testid}>
      <input ref={ref} type="file" accept={accept} capture={accept === "image/*" ? "environment" : undefined} multiple={!single} className="hidden" onChange={(e) => { handle(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
      {has ? (
        <div className="grid grid-cols-2 gap-2">
          {ids.map((id, i) => <Thumb key={id} id={id} testid={`${testid}-thumb-${i}`} onOpen={setOpen} onRemove={(x) => onChange((cur) => cur.filter((y) => y !== x))} />)}
          <button type="button" onClick={() => ref.current.click()} className="aspect-square rounded-md border border-dashed grid place-items-center text-slate-400 hover:text-[#0E2D43]" data-testid={`${testid}-add`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current.click()} className="aspect-[4/3] rounded-lg grid place-items-center text-slate-400 hover:text-[#0E2D43] hover:bg-slate-50 transition-colors" data-testid={`${testid}-capture`}>
          {busy ? <Loader2 className="w-7 h-7 animate-spin" /> : <Camera className="w-8 h-8" />}
        </button>
      )}
      <div className="text-xs font-bold text-[#0E2D43] text-center">{label}</div>
      <Lightbox id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

export function PhotoPicker({ items, onChange, categories, testid = "photo-picker" }) {
  const cam = useRef();
  const gal = useRef();
  const [open, setOpen] = useState(null);
  const { busy, handle } = useUploader((id) => onChange((cur) => [...cur, { file_id: id, category: "" }]));
  const pick = (e) => {
    handle(e.target.files);
    e.target.value = "";
  };
  return (
    <div data-testid={testid}>
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
      <input ref={gal} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`${testid}-gallery-input`} />
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button type="button" onClick={() => cam.current.click()} className="h-12 rounded-lg bg-[#0E2D43] text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid={`${testid}-camera`}>
          <Camera className="w-4 h-4" /> Prendre une photo
        </button>
        <button type="button" onClick={() => gal.current.click()} className="h-12 rounded-lg border-2 border-[#0E2D43] text-[#0E2D43] font-semibold text-sm flex items-center justify-center gap-2" data-testid={`${testid}-gallery`}>
          <ImagePlus className="w-4 h-4" /> Galerie
        </button>
      </div>
      {busy > 0 && <div className="text-xs text-slate-500 flex items-center gap-1 mb-2"><Loader2 className="w-3 h-3 animate-spin" /> Envoi de {busy} photo(s)…</div>}
      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {items.map((p, i) => (
            <Thumb key={p.file_id} id={p.file_id} testid={`${testid}-thumb-${i}`} onOpen={setOpen} onRemove={(x) => onChange((cur) => cur.filter((y) => y.file_id !== x))}>
              {categories && (
                <select value={p.category} onChange={(e) => onChange((cur) => cur.map((y) => (y.file_id === p.file_id ? { ...y, category: e.target.value } : y)))}
                  className="mt-1 w-full text-[11px] border rounded px-1 py-0.5 bg-white" data-testid={`${testid}-category-${i}`}>
                  {Object.entries(categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              )}
            </Thumb>
          ))}
        </div>
      )}
      <Lightbox id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

export function Gallery({ ids, testid = "gallery", labels }) {
  const [open, setOpen] = useState(null);
  if (!ids.length) return <div className="text-xs text-slate-400">Aucune photo</div>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid={testid}>
      {ids.map((id, i) => (
        <div key={id}>
          <Thumb id={id} testid={`${testid}-${i}`} onOpen={setOpen} />
          {labels?.[i] && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{labels[i]}</div>}
        </div>
      ))}
      <Lightbox id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
